/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { Task } from 'ember-concurrency';

import ApiErrorsService from 'cio/services/api-errors';
import IdentityModel from 'cio/models/identity';
import EnvironmentService from 'cio/services/environment';
import { IdentityMapping, IdentityMappingArgs } from 'cio/utils/identity/types';

interface TwilioIdentitiesMapArgs {
  twilioIdentities: IdentityModel[];
  saveButtonText: string;
  newIdentities: IdentityModel[];
  identitiesInUse: string[];
  setIdentitiesMap: (identitiesMap: IdentityMappingArgs) => void;
  cancelMapping: () => void;
  saveIdentityTask: Task<unknown, any[]>;
  needsMapping: boolean;
}

export default class TwilioIdentitiesMap extends Component<TwilioIdentitiesMapArgs> {
  @service environment!: EnvironmentService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;

  @tracked identities: IdentityMapping[] = [];
  @tracked isModalOpen = true;
  @tracked validationMessage: string | null = null;

  get saveButtonText(): string {
    return this.args.saveButtonText ?? 'Save Changes';
  }

  get identityOptions() {
    return this.args.newIdentities != null
      ? [
          { value: null, label: 'Do not map' },
          ...this.args.newIdentities.map((identity) => ({ value: identity, label: identity.phone })),
        ]
      : [];
  }

  get showModal() {
    return this.isModalOpen && this.args?.needsMapping;
  }

  get inUseIdentities(): IdentityModel[] {
    return this.args.twilioIdentities.filter((identity) => this.args.identitiesInUse.includes(identity.id));
  }

  @action
  closeModal() {
    this.args.cancelMapping();
  }

  @action
  validate() {
    this.validationMessage = this.validateAllInUseIdentitiesMapped();
  }

  @action
  validateReplacementUniqueness() {
    let replacementSet = new Set();
    if (!this.identities.length) {
      return 'No identities have been mapped.';
    }

    for (let { replacement } of this.identities) {
      let { phone: replacementId } = replacement;
      if (replacementSet.has(replacementId)) {
        return `Replacement identity ${replacementId} is used more than once.`;
      }
      replacementSet.add(replacementId);
    }

    return null;
  }

  @action
  validateAllInUseIdentitiesMapped() {
    let inUseIdentityIds = [...this.args.identitiesInUse];
    let mappedIdentityIds = this.identities.map((mapping) => mapping.identity.id);

    for (let id of inUseIdentityIds) {
      if (!mappedIdentityIds.includes(id)) {
        let idx = this.args.twilioIdentities.find((i) => i.id === id);
        if (idx == null) {
          return `Identity ${id} is in use but not mapped.`;
        }
        return `Identity ${idx?.phone} is in use but not mapped.`;
      }
    }

    return null;
  }

  @action
  async saveIdentity() {
    this.validate();
    if (this.validationMessage != null) {
      return;
    }

    await this.args.saveIdentityTask.perform();
  }

  getOnSelectedHandler = (identity: IdentityModel) => {
    return (selected: { label: string; value: IdentityModel }) => {
      let idx = this.identities.findIndex((i) => i.identity.id === identity.id);
      if (selected.value == null) {
        this.handleSkipMapping(idx);
        return;
      }
      this.handleMapping(identity, selected, idx);
    };
  };

  get isDisabled(): boolean {
    return this.validationMessage != null || this.args.saveIdentityTask?.isRunning;
  }

  private handleMapping(identity: IdentityModel, selected: { label: string; value: IdentityModel }, idx: number) {
    let newMapping = {
      identity: {
        id: identity.id,
        phone: identity.phone,
        name: identity.name,
      },
      replacement: selected.value,
    };

    if (idx === -1) {
      this.identities.push(newMapping);
    } else {
      this.identities[idx] = newMapping;
    }

    this.validate();
    this.args.setIdentitiesMap(this.identities);
  }

  private handleSkipMapping(idx: number) {
    if (idx !== -1) {
      this.identities.splice(idx, 1);
    }
    this.validate();
    this.args.setIdentitiesMap(this.identities);
  }
}
